import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "../../api/axios";
import "./Sidebar.css";

// ✅ UNCHANGED
const getInitials = (name, email) => {
  if (name) {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return "U";
};

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ UNCHANGED — permission state
  const [permissions, setPermissions] = useState({
    read_access: "",
    both_access: "",
    isAdmin: false,
  });
  const [loading, setLoading] = useState(true);

  // ✅ UNCHANGED — user/profile state
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [profileImage, setProfileImage] = useState(
    localStorage.getItem("profile_image") || ""
  );

  // ✅ UNCHANGED — notification state
  const [notifications, setNotifications] = useState([]);
  const [showNotif, setShowNotif] = useState(false);

  // ================================
  // ✅ UNCHANGED — PERMISSION LOGIC
  // ================================
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const res = await api.get(`/user-roles/me?t=${new Date().getTime()}`);

        if (res.data.isAdmin) {
          setPermissions({
            isAdmin: true,
            read_access: "ALL",
            both_access: "ALL",
          });
        } else if (res.data.noRoleDefined) {
          setPermissions({
            isAdmin: false,
            read_access:
              "Home, Dashboard, Kb, Artifacts, Analytics, Logs, Settings",
            both_access: "",
          });
        } else {
          setPermissions({
            isAdmin: false,
            read_access: res.data.read_access || "",
            both_access: res.data.both_access || "",
          });
        }
      } catch (err) {
        console.error("Sidebar permission check failed");
        setPermissions({ isAdmin: true, read_access: "ALL", both_access: "" });
      } finally {
        setLoading(false);
      }
    };
    fetchPermissions();
  }, []);

  // ================================
  // ✅ UNCHANGED — PROFILE + NOTIFICATION LOGIC
  // ================================

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    setProfileImage("");
    navigate("/login", { replace: true });
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/profile`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) {
        handleLogout();
        return;
      }

      const data = await res.json();
      setUser(data);
      setProfileImage(data.profile_image || "");
      localStorage.setItem("user", JSON.stringify(data));
      localStorage.setItem("profile_image", data.profile_image || "");
    } catch (err) {
      console.log("Sidebar profile error:", err);
    }
  };

  const fetchNotifications = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/notifications`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data);
    } catch (err) {
      console.log("Notification error:", err);
    }
  };

  const markAsRead = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/notifications/read`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (err) {
      console.log("Mark read error:", err);
    }
  };

  const handleBellClick = () => {
    if (!showNotif) {
      setShowNotif(true);
      return;
    }
    setShowNotif(false);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    markAsRead();
  };

  // ✅ UNCHANGED — event listeners
  useEffect(() => {
    const handleProfileUpdate = () => fetchProfile();
    const handleNotifUpdate = () => fetchNotifications();

    fetchProfile();
    fetchNotifications();

    window.addEventListener("profileImageUpdated", handleProfileUpdate);
    window.addEventListener("notificationUpdated", handleNotifUpdate);

    return () => {
      window.removeEventListener("profileImageUpdated", handleProfileUpdate);
      window.removeEventListener("notificationUpdated", handleNotifUpdate);
    };
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // ✅ UNCHANGED — fallback avatar
  const fallbackAvatar = user
    ? `https://ui-avatars.com/api/?name=${getInitials(
        user.name,
        user.email
      )}&size=64&background=4B7BF5&color=fff&bold=true`
    : "/images/Profile.svg";

  // ================================
  // ✅ UNCHANGED — MENU
  // ================================
  const fullMenu = [
    { icon: "newhome.svg",    label: "Home",           path: "/home",      alwaysVisible: true },
    { icon: "newdash.svg",    label: "Dashboard",      path: "/dashboard" },
    { icon: "newkb.svg",      label: "Knowledge Base", path: "/kb" },
    { icon: "newlytics.svg",  label: "Analytics",      path: "/analytics" },
    { icon: "newfacts.svg",   label: "Artifacts",      path: "/artifacts" },
    { icon: "newlogs.svg",    label: "Logs",           path: "/logs" },
  ];

  // Settings rendered separately on the right (next to bell) — see JSX below

  // ✅ UNCHANGED — permission filter
  const filteredMenu = fullMenu.filter((item) => {
    if (item.alwaysVisible || permissions.isAdmin) return true;

    const allowedRead = (permissions.read_access || "")
      .split(",")
      .map((s) => s.trim());

    const allowedBoth = (permissions.both_access || "")
      .split(",")
      .map((s) => s.trim());

    return (
      allowedRead.includes(item.label) ||
      allowedBoth.includes(item.label)
    );
  });

  // Settings visibility check (alwaysVisible in original)
  const showSettings =
    permissions.isAdmin ||
    (permissions.read_access || "").split(",").map((s) => s.trim()).includes("Settings") ||
    (permissions.both_access || "").split(",").map((s) => s.trim()).includes("Settings") ||
    true; // alwaysVisible: true in original menu

  if (loading) return <div className="navbar-loading" />;

  return (
    <nav className="app-navbar">

      {/* ── LEFT: Brand logo (not clickable) ── */}
      <div className="navbar-brand-wrap">
        <img src="/images/newbrand.svg" alt="logo" className="navbar-brand-logo" />
      </div>

      {/* ── CENTER: Nav items ── */}
      <ul className="navbar-nav-list">
        {filteredMenu.map((item, index) => {
          const isActive =
            location.pathname.toLowerCase() === item.path.toLowerCase();

          return (
            <li key={index}>
              <div
                className={`navbar-nav-item${isActive ? " active" : ""}`}
                onClick={() => navigate(item.path)}
              >
                <img
                  src={`/images/${item.icon}`}
                  alt={item.label}
                  className="navbar-nav-icon"
                />
                <span className="navbar-nav-label">{item.label}</span>
              </div>
            </li>
          );
        })}
      </ul>

      {/* ── RIGHT: Settings icon + Bell + Profile ── */}
      <div className="navbar-right">

        {/* ⚙️ Settings icon — matches design position (right side, before bell) */}
        {showSettings && (
          <button
            className={`navbar-icon-btn${location.pathname.toLowerCase() === "/settings" ? " navbar-icon-btn--active" : ""}`}
            onClick={() => navigate("/settings")}
            title="Settings"
          >
            <img src="/images/newset.svg" alt="Settings" className="navbar-settings-icon" />
          </button>
        )}

        {/* 🔔 Notification bell */}
        <div className="position-relative">
          <button className="navbar-icon-btn" onClick={handleBellClick}>
            <img src="/images/newnoti.svg" alt="Notifications" className="navbar-bell-icon" />
            {unreadCount > 0 && <span className="navbar-notif-badge" />}
          </button>

          {showNotif && (
            <div className="navbar-notif-dropdown">
              {notifications.filter((n) => !n.is_read).length === 0 ? (
                <div className="navbar-notif-empty">No notifications</div>
              ) : (
                notifications
                  .filter((n) => !n.is_read)
                  .map((n) => (
                    <div key={n.id} className="navbar-notif-item">
                      <div className="navbar-notif-title">{n.title}</div>
                      <div className="navbar-notif-msg">{n.message}</div>
                      <small className="navbar-notif-time">
                        {new Date(n.created_at).toLocaleString()}
                      </small>
                    </div>
                  ))
              )}
            </div>
          )}
        </div>

        {/* 👤 Profile */}
        <div className="dropdown">
          <button
            className="navbar-profile-btn dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <img
              src={profileImage || fallbackAvatar}
              alt="Profile"
              className="navbar-profile-avatar"
              onError={(e) => { e.target.src = fallbackAvatar; }}
            />
          </button>

          <ul className="dropdown-menu dropdown-menu-end">
            <li>
              <button
                className="dropdown-item"
                onClick={() => navigate("/settings")}
              >
                Profile
              </button>
            </li>
            <li><hr className="dropdown-divider" /></li>
            <li>
              <button
                className="dropdown-item text-danger"
                onClick={handleLogout}
              >
                Logout
              </button>
            </li>
          </ul>
        </div>

      </div>
    </nav>
  );
}

export default Sidebar;